import _ from 'lodash';
import { MoUtils, QueryStore, TemplatingStoreHelpers } from 'ui-common';
import { BeaconConfig, RetailCartAddSignalRequest, RetailClickSignalRequest, RetailPurchaseSignalRequest, RetailQuerySignalRequest, RetailQueryStoreConfig, RetailVisitSignalRequest, StoredSignalRequest } from '../services/interfaces';
import { v4 } from 'uuid';

export class RetailQueryStore extends QueryStore {
  /** Retail signal API reference https://github.com/lucidworks/events-analytics/blob/main/api/external-api.yaml */

  static bulkRequestStorageKey: string = 'lwBeaconPendingSignals';
  static sessionIdStorageKey: string = 'lwBeaconSessionId';
  static siteVisitStorageTimestampKey: string = 'lwBeaconSiteVisitTimestamp';
  static beaconActiveTimestampStorageKey: string = 'lwBeaconActiveTimestamp';

  protected beaconConfig: BeaconConfig;

  constructor(config: RetailQueryStoreConfig) {
    super(config);

    this.beaconConfig = config.beaconConfig;
  }

  override querySignal(request: RetailQuerySignalRequest) {
    // Required: searchTerms, queryId
    return this.sendRetailSignal({ 
      queryId: this.currentParams.queryId,
      ...request,
    }, 'retailQuerySignal');
  }

  override clickSignal(request: RetailClickSignalRequest) {
    // Required: productDetails, queryId
    return this.sendRetailSignal({ 
      queryId: this.currentParams.queryId,
      ...request,
    }, 'retailClickSignal');
  }

  /**
   * Send a site visit signal if the user has visited the site for the first time or after the configured threshold.
   * @param request 
   * @returns 
   */
  siteVisitSignal(request: RetailVisitSignalRequest) {
    const lastVisitTime = Number(localStorage.getItem(RetailQueryStore.siteVisitStorageTimestampKey));
    const currentTime = Date.now();
    const shouldSendVisitSignal = _.isNaN(lastVisitTime) || (currentTime - lastVisitTime > this.beaconConfig.visit.threshold);

    if (shouldSendVisitSignal) {
      localStorage.setItem(RetailQueryStore.siteVisitStorageTimestampKey, String(currentTime));
    }

    return shouldSendVisitSignal ? this.sendRetailSignal(request, 'retailVisitSignal') : Promise.resolve();
  }

  cartAddSignal(request: RetailCartAddSignalRequest) {
    // Required: cartId, productDetails, queryId
    return this.sendRetailSignal({ 
      queryId: this.currentParams.queryId,
      ...request,
    }, 'retailCartAddSignal');
  }

  purchaseSignal(request: RetailPurchaseSignalRequest) {
    return this.sendRetailSignal(request, 'retailPurchaseSignal');
  }

  /**
   * Execute/flush the pending requests stored in local storage. Make sure to trigger the next cycle
   * until the outstanding requests are proceeded.
   */
  async bulkRequest() {
    const operationQueue: StoredSignalRequest[] = MoUtils.safeParseJSON(localStorage[RetailQueryStore.bulkRequestStorageKey] || '[]');

    localStorage.removeItem(RetailQueryStore.bulkRequestStorageKey);

    await Promise.all(operationQueue.map(({ request, method })=> this.doSendRetailSignal(request, method)));
    await MoUtils.waitFor(this.config.bulkSignalsInterval || 0);

    this.bulkRequest();
  }

  sendRetailSignal(request: RetailVisitSignalRequest | RetailQuerySignalRequest | RetailClickSignalRequest | RetailCartAddSignalRequest | RetailPurchaseSignalRequest, method) {
    const bulkSignalsInterval = this.config.bulkSignalsInterval || 0;
    let result;

    if (bulkSignalsInterval) {
      this.storeSignalToBulkQueue({ request, method });
    } else {
      result = this.doSendRetailSignal(request, method);
    }
    
    return result;
  }

  storeSignalToBulkQueue(request) {
    let storedRequests = localStorage[RetailQueryStore.bulkRequestStorageKey] || '[]';

    storedRequests = MoUtils.safeParseJSON(storedRequests);
    storedRequests.push(request);
    localStorage[RetailQueryStore.bulkRequestStorageKey] = JSON.stringify(storedRequests);
  }

  doSendRetailSignal(request: RetailVisitSignalRequest| RetailQuerySignalRequest | RetailClickSignalRequest | RetailCartAddSignalRequest | RetailPurchaseSignalRequest, method) {
    const params = this.getSignalCommonParams();

    return (this.getApiService().invoke('retailSignal', method, { data: [_.merge(params, request)] }) || {})
  }

  getSignalCommonParams() {
    const timestamp = Date.now();

    const params = {
      ..._.omit(this.getDefaultRequestParams(), 'debug', 'utcOffset'),
      visitorId: TemplatingStoreHelpers.getVisitorId(),
      uri: MoUtils.getURI(),
      sourceId: 'beacon',
      timestamp,
    };

    localStorage.setItem(RetailQueryStore.beaconActiveTimestampStorageKey, String(timestamp));

    return params;
  }

  getOrCreateSession(): string {
    const lastActiveTimestamp = localStorage.getItem(RetailQueryStore.beaconActiveTimestampStorageKey);
    const currentTime = new Date().getTime();
    const sessionTimeoutThreshold = this.beaconConfig?.attributes.sessionTimeoutThreshold;
    let sessionId = localStorage.getItem(RetailQueryStore.sessionIdStorageKey);
  
    // Check if the last active timestamp is older than the configured session timeout threshold.
    if (lastActiveTimestamp && (currentTime - parseInt(lastActiveTimestamp) > sessionTimeoutThreshold)) {
      localStorage.removeItem(RetailQueryStore.sessionIdStorageKey);
      sessionId = null;
    }
  
    // If no sessionId exists, create a new one and store it.
    if (!sessionId) {
      sessionId = v4();
      localStorage.setItem(RetailQueryStore.sessionIdStorageKey, sessionId);
    }
  
    return sessionId;
  };

  protected override getDefaultRequestParams() {
    const params = super.getDefaultRequestParams();

    return {
      ...params,
      sessionId: this.getOrCreateSession(),
    };
  }
}
